/** Small shared presentation pieces: icons, badges, callouts, progress. */

import type { ReactNode } from 'react';
import type { CheckStatus, Verdict } from '../lib/types';

// ------------------------------------------------------------------- icons
type IconProps = { size?: number; className?: string };

const svgProps = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export const UploadIcon = ({ size = 30, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </svg>
);

export const FileIcon = ({ size = 20, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

export const ChevronIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

export const CloseIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const DownloadIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

export const PlusIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const ShieldIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const MailIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 6l10 7 10-7" />
  </svg>
);

export const InboxIcon = ({ size = 34, className }: IconProps) => (
  <svg {...svgProps(size)} className={className}>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

export const Logo = () => <img src="/clearifc-logo.png" alt="" className="logo-mark" />;

// ------------------------------------------------------------------ badges
const STATUS_LABEL: Record<CheckStatus, string> = {
  pass: 'Pass',
  fail: 'Fail',
  warning: 'Warning',
  skipped: 'Not applicable',
};

export function StatusBadge({ status, large }: { status: CheckStatus; large?: boolean }) {
  return (
    <span className={`badge badge-${status}${large ? ' badge-lg' : ''}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

export function Badge({
  children,
  variant = 'neutral',
}: {
  children: ReactNode;
  variant?: 'neutral' | 'pass' | 'fail' | 'warning' | 'skipped' | 'tier';
}) {
  return <span className={`badge badge-${variant}`}>{children}</span>;
}

// ---------------------------------------------------------------- callouts
export function Callout({
  variant = 'info',
  children,
}: {
  variant?: 'info' | 'error' | 'warn' | 'upsell';
  children: ReactNode;
}) {
  return (
    <div className={`callout callout-${variant}`} role={variant === 'error' ? 'alert' : undefined}>
      <div>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------- progress
export function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className="progress-track"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="progress-fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-hidden />;
}

// ----------------------------------------------------------------- verdict
export const VERDICT_LABEL: Record<Verdict, string> = {
  pass: 'Pass',
  pass_with_warnings: 'Pass with warnings',
  fail: 'Fail',
  error: 'Could not be validated',
};

// ------------------------------------------------------------------- utils
export function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatNumber(value: number): string {
  return value.toLocaleString();
}
